// ============================================================================
// PHI ATTACHMENTS API — Client-side callable wrappers for PHI Step 3 functions
//
// All PHI attachment operations are routed through Cloud Functions (Admin SDK).
// No direct Firestore or Storage reads from the client. Signed URLs are
// short-lived (upload: 15 min, download: 60 min) and generated server-side.
//
// Upload flow:
//   1. createAttachmentUploadUrl → { attachmentId, uploadUrl }
//   2. PUT file to uploadUrl directly from browser (fetch, no CF involvement)
//   3. finalizeAttachmentUpload(referralId, attachmentId) → { success, sizeBytes }
//
// UPLOAD roles (server): tenant_admin, intake_coordinator
// DOWNLOAD roles (server): tenant_admin, intake_coordinator, scheduler, specialist
// READ roles (server): same as DOWNLOAD
// ============================================================================

import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { getFirebaseApp } from './store.firebase.js';

let _functions = null;

const getFunctionsInstance = () => {
  if (!_functions) {
    const app = getFirebaseApp();
    if (app) _functions = getFunctions(app);
  }
  return _functions;
};

const call = async (name, data) => {
  const fn = getFunctionsInstance();
  if (!fn) throw new Error('Firebase Functions not initialized');
  const result = await httpsCallable(fn, name)(data);
  return result.data;
};

// ── createAttachmentUploadUrl ─────────────────────────────────────────────────

/**
 * Create a manifest doc (UPLOADING) + return a signed PUT URL.
 * After the browser PUT completes, call finalizeAttachmentUpload.
 *
 * @param {string} referralId
 * @param {{
 *   mimeType:        string,
 *   originalName:    string,
 *   type:            string,   // MIME-validation category: LABS|IMAGING|PDF|PHOTO|OTHER|CONSULT_DOCUMENT
 *   requestId?:      string,   // reworkRequest doc ID — enables exact-match resolution
 *   requestItemType?: string,  // canonical rework item type — fallback resolution key
 *   category?:       string,   // attachment category (e.g. 'CONSULT_DOCUMENT')
 *   purpose?:        string,   // attachment purpose (e.g. 'CLOSED_LOOP_EVIDENCE')
 * }} options
 * @returns {Promise<{ attachmentId: string, uploadUrl: string }>}
 */
export const createAttachmentUploadUrl = (referralId, { mimeType, originalName, type, requestId, requestItemType, category, purpose }) =>
  call('createAttachmentUploadUrl', { referralId, mimeType, originalName, type, requestId, requestItemType, category, purpose });

// ── finalizeAttachmentUpload ──────────────────────────────────────────────────

/**
 * Verify file landed in Storage; set manifest status to READY + populate sizeBytes.
 * Must be called after the browser PUT to the signed URL succeeds.
 * READY status is what triggers onAttachmentManifestReady_updateRework.
 * @param {string} referralId
 * @param {string} attachmentId
 * @returns {Promise<{ success: true, sizeBytes: number }>}
 */
export const finalizeAttachmentUpload = (referralId, attachmentId) =>
  call('finalizeAttachmentUpload', { referralId, attachmentId });

// ── createAttachmentDownloadUrl ───────────────────────────────────────────────

/**
 * Get a 60-min signed GET URL for a READY attachment.
 * Writes a VIEW_ATTACHMENT_DOWNLOAD_URL phi_access_log entry server-side.
 * @param {string} referralId
 * @param {string} attachmentId
 * @returns {Promise<{ downloadUrl: string, originalName: string, mimeType: string }>}
 */
export const createAttachmentDownloadUrl = (referralId, attachmentId) =>
  call('createAttachmentDownloadUrl', { referralId, attachmentId });

// ── listAttachments ───────────────────────────────────────────────────────────

/**
 * List all READY attachment manifests for a referral (ordered by createdAt asc).
 * Writes a LIST_ATTACHMENTS phi_access_log entry server-side.
 * @param {string} referralId
 * @returns {Promise<{ attachments: object[] }>}
 */
export const listAttachments = (referralId) =>
  call('listAttachments', { referralId });

// ── streamAttachmentDocument ─────────────────────────────────────────────────
// Fetches attachment bytes as a Blob via the onRequest streaming endpoint.
// The browser address bar never sees a storage.googleapis.com URL.

const STREAM_BASE_PATH = '/api/attachments/stream';

export async function fetchAttachmentBlob(referralId, attachmentId) {
  const app = getFirebaseApp();
  if (!app) throw new Error('Firebase not initialized');
  const auth = getAuth(app);
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const idToken = await user.getIdToken();

  const url = `${STREAM_BASE_PATH}?referralId=${encodeURIComponent(referralId)}&attachmentId=${encodeURIComponent(attachmentId)}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${idToken}` },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Stream failed (${res.status})`);
  }

  const blob = await res.blob();
  return { blob, mimeType: res.headers.get('Content-Type') || 'application/pdf' };
}

// ── createAttachmentViewSession ───────────────────────────────────────────────

export const createAttachmentViewSession = (referralId, attachmentId) =>
  call('createAttachmentViewSession', { referralId, attachmentId });

// ── fetchAttachmentBlobBySession ─────────────────────────────────────────────

const SESSION_STREAM_BASE_PATH = '/api/attachments/stream-session';

export async function fetchAttachmentBlobBySession(viewerSessionId) {
  const app = getFirebaseApp();
  if (!app) throw new Error('Firebase not initialized');
  const auth = getAuth(app);
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const idToken = await user.getIdToken();

  const url = `${SESSION_STREAM_BASE_PATH}?viewerSessionId=${encodeURIComponent(viewerSessionId)}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${idToken}` },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Stream failed (${res.status})`);
  }

  const blob = await res.blob();
  return { blob, mimeType: res.headers.get('Content-Type') || 'application/pdf' };
}

// ── generateReferralPacketPdf ─────────────────────────────────────────────────

/**
 * Generate the system referral packet PDF for a referral and attach it.
 *
 * Called automatically after successful Gate submission (best-effort).
 * Writes a manifest with:
 *   source:       SYSTEM_GENERATED
 *   artifactType: REFERRAL_PACKET_PDF
 *   status:       READY
 *
 * The manifest is visible to intake coordinator in Attachments.
 * It is explicitly excluded from rework request auto-resolution logic.
 *
 * @param {string} referralId
 * @returns {Promise<{ success: true, attachmentId: string }>}
 */
export const generateReferralPacketPdf = (referralId) =>
  call('generateReferralPacketPdf', { referralId });
